import axios from 'axios';

const api = axios.create({ baseURL: '/api/v1' });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

const upload = (url, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(url, fd);
};

export const authAPI = {
  login: (data) => api.post('/auth/login', new URLSearchParams(data)),
  me: () => api.get('/auth/me'),
  listUsers: () => api.get('/auth/users'),
  createUser: (data) => api.post('/auth/users', data),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const warehouseAPI = {
  list: () => api.get('/warehouses/'),
  create: (data) => api.post('/warehouses/', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  stock: (id) => api.get(`/warehouses/${id}/stock`),
  uploadImage: (id, file) => upload(`/warehouses/${id}/upload-image`, file),
};

export const rawMaterialAPI = {
  list: () => api.get('/raw-materials/'),
  lowStock: () => api.get('/raw-materials/low-stock'),
  create: (data) => api.post('/raw-materials/', data),
  update: (id, data) => api.put(`/raw-materials/${id}`, data),
  delete: (id) => api.delete(`/raw-materials/${id}`),
  adjust: (id, data) => api.post(`/raw-materials/${id}/adjust`, data),
  uploadImage: (id, file) => upload(`/raw-materials/${id}/upload-image`, file),
  removeImage: (id) => api.delete(`/raw-materials/${id}/image`),
};

export const finishedGoodAPI = {
  list: () => api.get('/finished-goods/'),
  create: (data) => api.post('/finished-goods/', data),
  update: (id, data) => api.put(`/finished-goods/${id}`, data),
  delete: (id) => api.delete(`/finished-goods/${id}`),
  uploadImage: (id, file) => upload(`/finished-goods/${id}/upload-image`, file),
  removeImage: (id) => api.delete(`/finished-goods/${id}/image`),
};

export const productionAPI = {
  list: () => api.get('/production/'),
  create: (data) => api.post('/production/', data),
  delete: (id) => api.delete(`/production/${id}`),
};

export const salesAPI = {
  listCustomers: () => api.get('/sales/customers'),
  createCustomer: (data) => api.post('/sales/customers', data),
  updateCustomer: (id, data) => api.put(`/sales/customers/${id}`, data),
  deleteCustomer: (id) => api.delete(`/sales/customers/${id}`),
  listPrices: () => api.get('/sales/prices'),
  setPrice: (data) => api.post('/sales/prices', data),
  deletePrice: (id) => api.delete(`/sales/prices/${id}`),
  list: (params) => api.get('/sales/', { params }),
  get: (id) => api.get(`/sales/${id}`),
  nextNumber: () => api.get('/sales/next-number'),
  create: (data) => api.post('/sales/', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  debts: () => api.get('/sales/debts'),
  markPaid: (id) => api.post(`/sales/${id}/mark-paid`),
  markUnpaid: (id) => api.post(`/sales/${id}/mark-unpaid`),
  receive: (id, data) => api.post(`/sales/${id}/receive`, data),
};

export const ladingAPI = {
  list: (params) => api.get('/lading-bills/', { params }),
  get: (id) => api.get(`/lading-bills/${id}`),
  create: (data) => api.post('/lading-bills/', data),
  update: (id, data) => api.put(`/lading-bills/${id}`, data),
  delete: (id) => api.delete(`/lading-bills/${id}`),
  uploadImage: (id, file) => upload(`/lading-bills/${id}/upload-image`, file),
};

export const transferAPI = {
  list: () => api.get('/transfers/'),
  create: (data) => api.post('/transfers/', data),
};

export const reportAPI = {
  summary: (params) => api.get('/reports/summary', { params }),
  production: (params) => api.get('/reports/production', { params }),
  sales: (params) => api.get('/reports/sales', { params }),
  ladingBills: (params) => api.get('/reports/lading-bills', { params }),
  products: (params) => api.get('/reports/products', { params }),
  rawMaterials: (params) => api.get('/reports/raw-materials', { params }),
  customers: (params) => api.get('/reports/customers', { params }),
  inventory: () => api.get('/reports/inventory'),
  pnl: (params) => api.get('/reports/pnl', { params }),
  auditLog: () => api.get('/reports/audit-log'),
  exportExcel: (params) => api.get('/reports/export/excel', { params, responseType: 'blob' }),
};

export const announcementAPI = {
  list: () => api.get('/announcements/'),
  create: (data) => api.post('/announcements/', data),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  delete: (id) => api.delete(`/announcements/${id}`),
};

export const inventoryAPI = {
  stock: (item_type) => api.get('/inventory/stock', { params: { item_type } }),
  setStock: (data) => api.put('/inventory/stock', data),
  movements: (params) => api.get('/inventory/movements', { params }),
  counts: () => api.get('/inventory/counts'),
  createCount: (data) => api.post('/inventory/counts', data),
};

export const accountingAPI = {
  currencies: () => api.get('/accounting/currencies'),
  createCurrency: (data) => api.post('/accounting/currencies', data),
  updateCurrency: (code, data) => api.put(`/accounting/currencies/${code}`, data),
  refreshRates: () => api.post('/accounting/currencies/refresh'),
  rateHistory: (code) => api.get(`/accounting/currencies/${code}/history`),
  accounts: () => api.get('/accounting/accounts'),
  createAccount: (data) => api.post('/accounting/accounts', data),
  updateAccount: (id, data) => api.put(`/accounting/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/accounting/accounts/${id}`),
  statement: (id, params) => api.get(`/accounting/accounts/${id}/statement`, { params }),
  transactions: (params) => api.get('/accounting/transactions', { params }),
  createTransaction: (data) => api.post('/accounting/transactions', data),
  deleteTransaction: (id) => api.delete(`/accounting/transactions/${id}`),
  transfer: (data) => api.post('/accounting/transfers', data),
  suppliers: () => api.get('/accounting/suppliers'),
  createSupplier: (data) => api.post('/accounting/suppliers', data),
  updateSupplier: (id, data) => api.put(`/accounting/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/accounting/suppliers/${id}`),
  ledger: (params) => api.get('/accounting/ledger', { params }),
  summary: () => api.get('/accounting/summary'),
};

export const purchaseAPI = {
  list: (params) => api.get('/purchases/', { params }),
  get: (id) => api.get(`/purchases/${id}`),
  nextNumber: () => api.get('/purchases/next-number'),
  create: (data) => api.post('/purchases/', data),
  pay: (id, data) => api.post(`/purchases/${id}/pay`, data),
  markPaid: (id) => api.post(`/purchases/${id}/mark-paid`),
  markUnpaid: (id) => api.post(`/purchases/${id}/mark-unpaid`),
  delete: (id) => api.delete(`/purchases/${id}`),
};

export const chatAPI = {
  users: () => api.get('/chat/users'),
  conversations: () => api.get('/chat/conversations'),
  create: (data) => api.post('/chat/conversations', data),
  update: (id, data) => api.put(`/chat/conversations/${id}`, data),
  leave: (id) => api.post(`/chat/conversations/${id}/leave`),
  messages: (id, params) => api.get(`/chat/conversations/${id}/messages`, { params }),
  send: (id, formData, onUploadProgress) => api.post(`/chat/conversations/${id}/messages`, formData, { onUploadProgress }),
  remove: (msgId) => api.delete(`/chat/messages/${msgId}`),
  read: (id, upto) => api.post(`/chat/conversations/${id}/read`, { upto }),
  typing: (id) => api.post(`/chat/conversations/${id}/typing`),
  poll: (params) => api.get('/chat/poll', { params }),
  refs: (type, q) => api.get('/chat/refs', { params: { type, q } }),
};
